import { UMAMI_SCRIPT_URL, UMAMI_WEBSITE_ID } from './umami-config'

interface Pageview {
  website: string
  hostname: string
  url: string
  title: string
  referrer: string
  name?: UsageEvent
}

interface Umami {
  track: (pageview: Pageview) => unknown
}

interface TrackerConfig {
  websiteId: string
  scriptUrl: string
  enabled: boolean
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const PUBLIC_PATH = /^\/(?:[a-z0-9_-]+(?:\/[a-z0-9_-]+)*)?$/i
// The page owner validates the public Nextra registry before mounting. Guard
// actual root namespaces here; an inner term such as /reference/api is public.
const PRIVATE_NAMESPACE =
  /^\/(?:api|auth|login|logout|reset(?:-password)?|password-reset|forgot-password|checkout|cart|account|my-account|private|admin|preview|wp-admin|wp-login)(?:\/|$)/i

export const USAGE_EVENTS = ['search_results', 'search_empty', 'search_result_open', 'example_copy', 'page_copy', 'download_start', 'correction_open'] as const
export type UsageEvent = (typeof USAGE_EVENTS)[number]
export function isUsageEvent(value: unknown): value is UsageEvent {
  return USAGE_EVENTS.some((name) => name === value)
}

export function emitUsageEvent(name: UsageEvent): void {
  document.dispatchEvent(new CustomEvent('fivemdocs:usage', { detail: name }))
}

export function createPublicPageviewTracker(config: TrackerConfig) {
  let loading: Promise<Umami | null> | undefined
  let lastSent: { path: string; visit: object } | undefined

  function eligible(path: string, requireCurrentPath = true): boolean {
    if (typeof window === 'undefined' || !config.enabled || !UUID.test(config.websiteId))
      return false
    if (window.location.origin !== 'https://fivemdocs.com' || (requireCurrentPath && window.location.pathname !== path))
      return false
    if (!PUBLIC_PATH.test(path) || PRIVATE_NAMESPACE.test(path)) return false
    const browser = navigator as Navigator & {
      globalPrivacyControl?: boolean
      msDoNotTrack?: string | number
    }
    const legacy = window as Window & { doNotTrack?: string | number }
    return (
      !browser.globalPrivacyControl &&
      ![browser.doNotTrack, browser.msDoNotTrack, legacy.doNotTrack].some(
        (value) => value === 1 || value === '1' || value === 'yes'
      )
    )
  }

  function load(): Promise<Umami | null> {
    if (loading) return loading
    const browser = window as Window & { umami?: Umami }
    // Do not add a second tracker, including one injected outside this repo.
    if (browser.umami || document.querySelector('script[data-website-id]'))
      return Promise.resolve(null)

    loading = new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = config.scriptUrl
      script.defer = true
      script.referrerPolicy = 'no-referrer'
      script.dataset.websiteId = config.websiteId
      script.dataset.domains = 'fivemdocs.com'
      script.dataset.autoTrack = 'false'
      script.dataset.doNotTrack = 'true'
      script.dataset.excludeSearch = 'true'
      script.dataset.excludeHash = 'true'
      script.dataset.fivemdocsUmami = 'true'
      script.addEventListener('load', () => resolve(browser.umami ?? null), { once: true })
      script.addEventListener('error', () => resolve(null), { once: true })
      document.head.appendChild(script)
    })
    return loading
  }

  async function trackPublicPage(
    path: string,
    title: string,
    isCurrent: () => boolean,
    visit: object
  ): Promise<boolean> {
    if (!eligible(path) || !isCurrent()) return false
    const umami = await load()
    if (!umami || !eligible(path) || !isCurrent()) return false
    if (lastSent?.path === path && lastSent.visit === visit) return false
    // Mark the visit before the network promise settles (including Strict Mode).
    lastSent = { path, visit }
    try {
      // Object tracking supplies only fixed public metadata, never browser URLs.
      await umami.track({
        website: config.websiteId,
        hostname: 'fivemdocs.com',
        url: path,
        title,
        referrer: '',
      })
      return true
    } catch {
      return false
    }
  }

  async function event(path: string, title: string, name: UsageEvent, isCurrent: () => boolean): Promise<boolean> {
    if (!isUsageEvent(name) || !eligible(path) || !isCurrent()) return false
    const umami = await load()
    // The action happened on a validated public page. Keep that fixed snapshot
    // across SPA navigation, while checking origin and privacy preferences again.
    if (!umami || !eligible(path, false)) return false
    try {
      // String/event shorthand includes browser defaults. Use an explicit payload
      // so search queries, fragments and referrers never reach the collector.
      await umami.track({ website: config.websiteId, hostname: 'fivemdocs.com', url: path, title, referrer: '', name })
      return true
    } catch {
      return false
    }
  }
  return Object.assign(trackPublicPage, { event })
}

export const trackPublicPage = createPublicPageviewTracker({
  websiteId: UMAMI_WEBSITE_ID,
  scriptUrl: UMAMI_SCRIPT_URL,
  enabled: process.env.NODE_ENV === 'production',
})
